import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import CustomAxios from "../utils/CustomAxios";
import {
  updateConrtol,
  updateEmail,
  updateFirstName,
  updateId,
  updateLastName,
  updateMpinOptional,
  updateMpinRequirements,
  updateRole,
} from "../redux/user_reducer";

const streamerColors = ["#00C2FF", "#00E3AF", "#FFD84D", "#111827", "#34D399"];
const streamerAngles = [-6, 10, 24, 38, 54, 72, 90, 110];

const CornerStreamers = ({ corner }) => {
  const horizontalSign = corner.includes("right") ? -1 : 1;
  const verticalSign = corner.includes("bottom") ? -1 : 1;
  const anchorStyles = {
    top: corner.includes("top") ? "-6%" : "auto",
    bottom: corner.includes("bottom") ? "-6%" : "auto",
    left: corner.includes("left") ? "-6%" : "auto",
    right: corner.includes("right") ? "-6%" : "auto",
  };

  return (
    <Box
      sx={{
        position: "absolute",
        width: { xs: 240, md: 360 },
        height: { xs: 240, md: 360 },
        pointerEvents: "none",
        zIndex: 0,
        ...anchorStyles,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0,227,175,0.16) 0%, rgba(0,194,255,0.08) 38%, rgba(255,255,255,0) 72%)",
        }}
      />
      {streamerAngles.map((angle, index) => {
        const travel = 60 + index * 12;
        const x = Math.cos((angle * Math.PI) / 180) * travel * horizontalSign;
        const y = Math.sin((angle * Math.PI) / 180) * travel * verticalSign;
        return (
          <Box
            key={`${corner}-${angle}`}
            component={motion.div}
            initial={{ opacity: 0, x: 0, y: 0, scaleY: 0.3, rotate: angle * horizontalSign }}
            animate={{
              opacity: [0, 1, 1, 0],
              x: [0, x, x * 1.18],
              y: [0, y, y * 1.12],
              scaleY: [0.3, 1, 1, 0.75],
              rotate: [
                angle * horizontalSign,
                (angle + 12) * horizontalSign,
                (angle + 24) * horizontalSign,
              ],
            }}
            transition={{
              duration: 1.6,
              repeat: Infinity,
              repeatDelay: 0.9,
              ease: "easeOut",
              delay: index * 0.05,
            }}
            sx={{
              position: "absolute",
              width: index % 2 === 0 ? 12 : 8,
              height: index % 2 === 0 ? 96 : 74,
              borderRadius: "999px",
              background:
                index % 2 === 0
                  ? `linear-gradient(180deg, ${streamerColors[index % streamerColors.length]}, rgba(255,255,255,0.9))`
                  : `linear-gradient(180deg, rgba(255,255,255,0.92), ${streamerColors[index % streamerColors.length]})`,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
              top: "50%",
              left: "50%",
              transformOrigin: "center",
            }}
          />
        );
      })}
    </Box>
  );
};

const PaymentProcessing = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const [statusText, setStatusText] = useState("We are setting up your account.");
  const signupSessionId = useMemo(
    () => searchParams.get("signup_session_id") || localStorage.getItem("signupSessionId") || "",
    [searchParams]
  );
  useEffect(() => {
    let isMounted = true;
    let pollTimer;

    const completeLogin = (data) => {
      if (!data?.access_token || !data?.refresh_token) {
        return false;
      }
      const requirements = data?.requirements || {
        mpin_setup_required: false,
        mpin_change_required: false,
        mpin_force_reset_required: false,
      };
      const mpinOptional =
        typeof data?.mpin_optional === "boolean" ? data.mpin_optional : true;
      const decoded = jwtDecode(data.access_token);
      localStorage.setItem("authtoken", data.access_token);
      localStorage.setItem("refreshToken", data.refresh_token);
      localStorage.removeItem("signupSessionId");
      dispatch(updateFirstName(decoded.first_name));
      dispatch(updateLastName(decoded.last_name));
      dispatch(updateEmail(decoded.email));
      dispatch(updateRole(decoded.role));
      dispatch(updateConrtol(decoded.control));
      dispatch(updateId(decoded.id));
      dispatch(updateMpinRequirements(requirements));
      dispatch(updateMpinOptional(mpinOptional));
      navigate("/dashboard", { replace: true });
      return true;
    };

    const pollStatus = async () => {
      if (!signupSessionId) {
        if (isMounted) {
          setError("Signup session not found.");
        }
        return;
      }
      try {
        const { data } = await CustomAxios.get(`signup-session/${signupSessionId}/status/`);
        if (!isMounted) {
          return;
        }
        if (data.status === "completed" && completeLogin(data)) {
          return;
        }
        if (data.status === "failed") {
          setError("We could not complete your signup. Please contact support.");
          return;
        }
        pollTimer = window.setTimeout(pollStatus, 2500);
      } catch (requestError) {
        if (!isMounted) {
          return;
        }
        setError("We are still confirming your access. Please wait.");
        pollStatus();
      }
    };

    setStatusText("Payment received. We are finalizing your access. Please wait.");
    pollStatus();

    return () => {
      isMounted = false;
      if (pollTimer) {
        window.clearTimeout(pollTimer);
      }
    };
  }, [dispatch, navigate, signupSessionId]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(180deg, #F5F7FA 0%, #EEF6F9 42%, #F5F7FA 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: { xs: 120, md: 160 },
          background: "linear-gradient(90deg, #00C2FF 0%, #00E3AF 100%)",
          opacity: 0.12,
        }}
      />
      <CornerStreamers corner="top-left" />
      <CornerStreamers corner="top-right" />
      <CornerStreamers corner="bottom-left" />
      <CornerStreamers corner="bottom-right" />

      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "min(580px, calc(100vw - 32px))",
          borderRadius: "28px",
          padding: { xs: "28px", sm: "42px" },
          background:
            "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(255,255,255,0.9))",
          border: "1px solid rgba(0, 194, 255, 0.14)",
          boxShadow: "0 24px 72px rgba(15, 23, 42, 0.1)",
          textAlign: "center",
          backdropFilter: "blur(12px)",
        }}
      >
        <Box
          sx={{
            width: 88,
            height: 6,
            borderRadius: "999px",
            mx: "auto",
            mb: 3,
            background: "linear-gradient(90deg, #00C2FF 0%, #00E3AF 100%)",
          }}
        />
        <Typography
          sx={{
            fontSize: { xs: "30px", sm: "40px" },
            lineHeight: 1.1,
            fontWeight: 800,
            color: "#0F172A",
          }}
        >
          Payment received
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            fontSize: { xs: "14px", sm: "16px" },
            lineHeight: "28px",
            color: error ? "#B42318" : "#475467",
          }}
        >
          {error || statusText}
        </Typography>
        {!error && (
          <Box
            sx={{
              mt: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress size={30} sx={{ color: "#00C2FF" }} />
            <Typography sx={{ color: "#0F172A", fontWeight: 600, fontSize: "14px" }}>
              Activating your access
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PaymentProcessing;
